import React, {useEffect, useState} from 'react'
import {Block, Col, Row} from 'jsxstyle'
import FormInput from '../../components/FormInput'
import XpertKnowledgeTutorialSection from '../XpertKnowledgeTutorial/components/XpertKnowledgeTutorialSection'
import Loader from '../../components/Loader'
import {ReactComponent as DeleteIcon} from '../../common/assets/delete-icon.svg'
import XpertKnowledgeSidebar from './../XpertKnowledgeTutorial/components/XpertKnowledgeTutorialSidebar'

import prompts from '../../common/assets/prompts.json'
import {useXpertKnowledgeTryPromptContextProvider, XpertKnowledgeTryPromptContextProvider} from './XpertKnowledgeTryPromptContext'
import {useNavigate, useParams} from 'react-router-dom'
import {useXpertKnowledgeAuthContext} from '../../components/AuthContext'
import {UserRole} from '../../services/User/type'
import i18n from '../../common/i18n'
import {LlmType} from '../../services/XpertKnowledge/type'
import {useXpertKnowledgeTutorialContext, XpertKnowledgeTutorialContextProvider} from '../XpertKnowledgeTutorial/XpertKnowledgeTutorialContext'

const XpertKnowledgeTryPrompt: React.FC = () => {
  const {xpertKnowledgeId} = useParams<{xpertKnowledgeId: string}>()
  const {user} = useXpertKnowledgeAuthContext()
  const {isTryPromptModalLoading, emulatedXpertKnowledgeSection, tryPromptXpertKnowledgeTutorial, setIsTryPromptModalLoading} =
    useXpertKnowledgeTryPromptContextProvider()
  const {selectedXpertKnowledgeTutorial} = useXpertKnowledgeTutorialContext()
  const [systemPrompt, setSystemPrompt] = useState<string>(prompts.systemPrompt)
  const [userPrompt, setUserPrompt] = useState<string>(prompts.userPrompt)
  const [jsonSchema, setJsonSchema] = useState<Record<string, any>>(prompts.schemajson)
  const [openTryPromptResult, setOpenTryPromptResult] = useState(false)
  const [temperature, setTemperature] = useState<number>(0.0)
  const [llmModel, setLlmModel] = useState<LlmType>(LlmType.GPT_4o)

  const navigate = useNavigate()

  useEffect(() => {
    setSystemPrompt(prompts.systemPrompt)
    setUserPrompt(prompts.userPrompt)
    setJsonSchema(prompts.schemajson)
  }, [xpertKnowledgeId])

  const handleTryPrompt = async () => {
    setIsTryPromptModalLoading(true)
    try {
      await tryPromptXpertKnowledgeTutorial({
        systemPrompt,
        userPrompt,
        jsonSchema,
        temperature,
        llmModel,
      })
      setOpenTryPromptResult(true)
    } catch (error) {
      console.error('Error trying prompt:', error)
    } finally {
      setIsTryPromptModalLoading(false)
    }
  }

  const handleEditPrompts = () => {
    setOpenTryPromptResult(false)
  }

  if (xpertKnowledgeId && user?.role !== UserRole.ADMIN_SIGHTCALL) {
    return (
      <Col height="100vh" justifyContent="center" alignItems="center" gap="16px" backgroundColor="#f8d7da" padding="20px">
        <Block>
          <DeleteIcon width="80px" height="80px" fill="#721c24" />
        </Block>
        <Block fontSize="32px" fontWeight="bold" color="#721c24">
          {i18n.t('xk.tutorial.assets.error')}
        </Block>
        <Block fontSize="18px" color="#721c24">
          {i18n.t('xk.tutorial.debug.noPermission')}
        </Block>
        <Block height="1px" width="50%" backgroundColor="#721c24" margin="12px 0" />
        <Block fontSize="16px" color="#721c24">
          {i18n.t('xk.tutorial.debug.contactTeam')}
        </Block>
      </Col>
    )
  }

  const firstSectionId =
    emulatedXpertKnowledgeSection.length > 0
      ? emulatedXpertKnowledgeSection.reduce((minSection, currentSection) =>
          currentSection.position < minSection.position ? currentSection : minSection
        ).id
      : undefined

  const [selectedSectionId, setSelectedSectionId] = useState<number | undefined>(firstSectionId)
  const [selectedSubSectionId, setSelectedSubSectionId] = useState<number | undefined>()

  return (
    <Col width="95%" height="100%" backgroundColor="#FFFFFF" padding="20px" overflow="scroll">
      <Row justifyContent="center" alignItems="center" padding="0 0 30px 0" borderBottom="2px solid #EEEEEE" marginBottom="20px">
        <Block fontSize="28px" fontWeight={700} color="#100E11" textAlign="center">
          {i18n.t('xk.tutorial.debug.title')}
        </Block>
      </Row>

      {isTryPromptModalLoading ? (
        <Col alignItems="center" height="20vh" marginTop="20px">
          <Loader size={75} />
          <Block fontWeight={500} fontSize="34px">
            {i18n.t('xk.tutorial.debug.loading')}
          </Block>
        </Col>
      ) : openTryPromptResult ? (
        <Col overflow="auto" height="100%" gap="20px" padding="5px">
          <Row justifyContent="space-between" alignItems="center" padding="0 0 20px 0">
            <Block fontSize="24px" fontWeight={600} color="#100E11">
              {i18n.t('xk.tutorial.debug.tryPromptResults')}
            </Block>
            <Block
              props={{onClick: handleEditPrompts}}
              backgroundColor="#007bff"
              color="white"
              borderRadius="6px"
              padding="12px 24px"
              fontSize="16px"
              fontWeight={500}
              cursor="pointer"
              transition="background-color 0.2s"
              userSelect="none"
              hoverBackgroundColor="#0056b3"
            >
              {i18n.t('xk.tutorial.debug.editPrompts')}
            </Block>
          </Row>
          <Row height="100%" gap="8px">
            {selectedXpertKnowledgeTutorial && (
              <XpertKnowledgeSidebar
                selectedSectionId={selectedSectionId ?? firstSectionId}
                setSelectedSectionId={setSelectedSectionId}
                selectedSubSectionId={selectedSubSectionId}
                setSelectedSubSectionId={setSelectedSubSectionId}
                selectedXpertKnowledgeTutorial={selectedXpertKnowledgeTutorial}
              />
            )}
            <Col gap="12px">
              {emulatedXpertKnowledgeSection.map((section: any, index: number) => (
                <XpertKnowledgeTutorialSection key={section.title ?? index} xpertKnowledgeSection={section} />
              ))}
            </Col>
          </Row>
        </Col>
      ) : (
        <Col width="100%" height="100%" gap="20px" padding="15px">
          <Row gap="30px" flexWrap="wrap">
            <Col
              flex="1"
              minWidth="300px"
              gap="20px"
              backgroundColor="rgba(255, 255, 255, 0.9)"
              borderRadius="20px"
              padding="30px"
              boxShadow="0 10px 30px rgba(0, 0, 0, 0.08)"
              backdropFilter="blur(10px)"
            >
              <Block fontSize="20px" fontWeight={700} color="#2d3748">
                {i18n.t('xk.tutorial.debug.temperature')}
              </Block>
              <Col gap="15px">
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                  style={{
                    width: '100%',
                    height: '8px',
                    borderRadius: '5px',
                    background: `linear-gradient(to right, #667eea 0%, #667eea ${(temperature / 2) * 100}%, #e2e8f0 ${
                      (temperature / 2) * 100
                    }%, #e2e8f0 100%)`,
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                />
                <Row justifyContent="space-between" alignItems="center">
                  <Block fontSize="14px" color="#718096" fontWeight={500}>
                    0.0 (Deterministic)
                  </Block>
                  <Block
                    fontSize="18px"
                    fontWeight={700}
                    color="#667eea"
                    backgroundColor="rgba(102, 126, 234, 0.1)"
                    padding="8px 16px"
                    borderRadius="10px"
                  >
                    {temperature.toFixed(1)}
                  </Block>
                  <Block fontSize="14px" color="#718096" fontWeight={500}>
                    2.0 (Creative)
                  </Block>
                </Row>
              </Col>
            </Col>

            <Col
              flex="1"
              minWidth="300px"
              gap="20px"
              backgroundColor="rgba(255, 255, 255, 0.9)"
              borderRadius="20px"
              padding="30px"
              boxShadow="0 10px 30px rgba(0, 0, 0, 0.08)"
              backdropFilter="blur(10px)"
            >
              <Block fontSize="20px" fontWeight={700} color="#2d3748">
                {i18n.t('xk.tutorial.debug.llmModel')}
              </Block>
              <select
                value={llmModel}
                onChange={(e) => setLlmModel(e.target.value as LlmType)}
                style={{
                  padding: '15px 20px',
                  fontSize: '16px',
                  fontWeight: '500',
                  borderRadius: '15px',
                  border: '2px solid #e2e8f0',
                  backgroundColor: 'white',
                  color: '#2d3748',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  outline: 'none',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea'
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0'
                  e.target.style.boxShadow = 'none'
                }}
              >
                {Object.values(LlmType).map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </Col>
          </Row>
          <Block fontSize="22px" fontWeight={600} color="#100E11" padding="0 0 10px 0">
            {i18n.t('xk.tutorial.debug.configurePrompts')}
          </Block>

          <Col gap="12px" backgroundColor="#EEEEEE" borderRadius="8px" padding="12px 16px">
            <Block fontSize="16px" fontWeight={600} color="#100E11">
              {i18n.t('xk.tutorial.debug.systemPrompt')}
            </Block>
            <FormInput
              placeholder={i18n.t('xk.tutorial.debug.systemPromptPlaceholder')}
              value={systemPrompt}
              onSubmit={(value) => setSystemPrompt(value)}
              debounce={100}
              error={''}
            />
          </Col>
          <Col gap="12px" backgroundColor="#EEEEEE" borderRadius="8px" padding="12px 16px">
            <Block fontSize="16px" fontWeight={600} color="#100E11">
              {i18n.t('xk.tutorial.debug.userPrompt')}
            </Block>
            <FormInput
              placeholder={i18n.t('xk.tutorial.debug.userPromptPlaceholder')}
              value={userPrompt}
              onSubmit={(value) => setUserPrompt(value)}
              debounce={100}
              error={''}
            />
          </Col>
          <Col gap="12px" backgroundColor="#EEEEEE" borderRadius="8px" padding="12px 16px">
            <Block fontSize="16px" fontWeight={600} color="#100E11">
              {i18n.t('xk.tutorial.debug.schemaJson')}
            </Block>
            <FormInput
              placeholder={i18n.t('xk.tutorial.debug.schemaJsonPlaceholder')}
              value={JSON.stringify(jsonSchema, null, 2)}
              onSubmit={(value) => {
                try {
                  const parsedValue = JSON.parse(value)
                  setJsonSchema(parsedValue)
                } catch (e) {
                  console.error('Invalid JSON format', e)
                }
              }}
              debounce={100}
              error={''}
            />
          </Col>

          <Row justifyContent="space-between" alignItems="center" padding="20px 0">
            <Block
              props={{onClick: () => navigate(`/xpert-knowledge/${xpertKnowledgeId}/tutorial`)}}
              backgroundColor="#6c757d"
              color="white"
              borderRadius="6px"
              padding="12px 24px"
              fontSize="16px"
              fontWeight={500}
              cursor="pointer"
              transition="background-color 0.2s"
              userSelect="none"
              hoverBackgroundColor="#545b62"
            >
              {i18n.t('xk.tutorial.debug.backToTutorial')}
            </Block>

            <Block
              props={{onClick: !isTryPromptModalLoading ? handleTryPrompt : undefined}}
              backgroundColor={isTryPromptModalLoading ? '#cccccc' : '#28a745'}
              color="white"
              borderRadius="6px"
              padding="12px 24px"
              fontSize="16px"
              fontWeight={500}
              cursor={isTryPromptModalLoading ? 'not-allowed' : 'pointer'}
              transition="background-color 0.2s"
              userSelect="none"
              hoverBackgroundColor={isTryPromptModalLoading ? '#cccccc' : '#1e7e34'}
              opacity={isTryPromptModalLoading ? 0.6 : 1}
            >
              {isTryPromptModalLoading ? i18n.t('xk.tutorial.debug.processing') : i18n.t('xk.tutorial.debug.tryPrompt')}
            </Block>
          </Row>
        </Col>
      )}
    </Col>
  )
}

export default () => (
  <XpertKnowledgeTutorialContextProvider>
    <XpertKnowledgeTryPromptContextProvider>
      <XpertKnowledgeTryPrompt />
    </XpertKnowledgeTryPromptContextProvider>
  </XpertKnowledgeTutorialContextProvider>
)
